import { Injectable } from '@angular/core';
import dexfreight, { Microservices } from '../../microservices';
import { HttpClient } from '@angular/common/http';
import { MainService } from '../main/main.service';
import { EnumsService } from 'src/app/enums.service';
import { DrayageFrontend } from 'src/app/interfaces/Drayage.interface';
import type { DraftFeedbackConversation, DraftFeedbackTurn } from 'src/app/interfaces/DraftFeedback.interface';
import { AuthService } from './auth.service';
import { Subject, Subscription, distinct, firstValueFrom, toArray } from 'rxjs';
import { NotificationsService } from '../notification/notifications.service';
import { NotificationTag } from 'src/app/@enums/NotificationTag.enum';
import { Repository } from 'src/app/@core/classes/Repository.class';
import { DbService } from 'src/app/@core/db.service';
import { $assert } from 'src/app/@core/classes/assert';
import debug from 'debug';
import { DataCache } from 'src/app/@core/classes/DataCache.class';
import { CacheListener } from 'src/app/@core/classes/CacheListener.class';

const PAGE_SIZE = 20;
const log = debug('@drayages');

@Injectable({
  providedIn: 'root'
})
export class DrayageService {

  private drayagesRepo: Repository<DrayageFrontend>;
  private drayages: Record<string, CacheListener<DrayageFrontend>> = {};
  private draftListeners: Record<string, CacheListener<DrayageFrontend | null>> = {};
  private eventsList: Subject<{ action: string, drayage: DrayageFrontend }> = new Subject();
  private _feed?: Subject<DrayageFrontend>;

  updates: Subject<DrayageFrontend> = new Subject();
  feed: Subject<DrayageFrontend> = new Subject();

  listeners: Subscription[] = [];
  allServices: any[] = [];

  cachedDrayages(myDrayages: boolean = false) {
    return DataCache.get<DrayageFrontend[]>(`drayageList/initialDrayages/${myDrayages}`) || [];
  }

  async createDraftDrayage() {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await (dexfreight as any).Draft.create({}, token);
  }

  constructor(
    public micro: Microservices,
    private _ms: MainService,
    public http: HttpClient,
    private enumsService: EnumsService,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private db: DbService
  ) {
    this.drayagesRepo = new Repository<DrayageFrontend>({
      sync: this.syncDrayage.bind(this),
      db: this.db.drayages
    });
  }

  async syncDrayage(drayageId: string | DrayageFrontend) {
    if (typeof drayageId === 'string') {
      const token = await this.authService.token.get();

      if (!token) return null;

      const drayage = await dexfreight.Drayage.getByIdDrayage(drayageId, token, true, true);

      log(`Drayage ${drayageId} synced`);
      this.db.drayages.put(drayage);

      return drayage;
    }

    await this.db.drayages.put(drayageId);
    log(`Drayage ${drayageId._id} synced from local object`);
    return drayageId;
  }

  setupListeners() {
    this.listeners.push(
      this.authService.token.onClear(this.clear.bind(this)),
      this.authService.token.onValue(token => this.setupFeed(token)),
      this.enumsService.drayageServices.onValue(services => this.allServices = services),
      this.notificationsService.notifications.subscribe(async notification => {
        if (notification.tags.includes(NotificationTag.drayageUpdate)) {
          if (!notification.data.drayageId) {
            console.log("Could not update drayage, could not find drayageId in notification data", notification);
            return;
          }

          if (!await this.drayagesRepo.localValue(notification.data.drayageId)) {
            console.log("Could not update drayage, could not find drayageId in drayages", notification);
            return;
          }

          this.sync(notification.data.drayageId);
        }
      })
    );
  }

  async setupFeed(token: string) {
    if (this._feed) this.clearFeed();

    const feed = await this.micro.Obs.Drayage.Rx.drayageFeed(token) as Subject<DrayageFrontend>;

    feed.subscribe(drayage => {
      this.eventsList.next({ action: 'insert', drayage })
      this.feed.next(drayage)
    });
    this._feed = feed;
  }

  clearFeed() {
    this._feed?.complete();
    this._feed = undefined;
  }

  clear() {
    this.clearAllDrayages();
    this.clearFeed();
  }

  removeListeners() {
    this.listeners.forEach(listener => listener.unsubscribe());
  }

  async observer(drayageId: string) {
    return await this.drayagesRepo.listener(drayageId);
  }

  async draftObserver(draftId: string) {
    if (!this.draftListeners[draftId]) {
      this.draftListeners[draftId] = this.createDraftListener(draftId);
    }

    await this.draftListeners[draftId].sync();
    return this.draftListeners[draftId];
  }

  async getDraft(draftId: string) {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await (dexfreight as any).Draft.get(draftId, token);
  }

  async editDraftDrayage(draftId: string, payload: any) {
    const token = await this.authService.token.get();
    if (!token) return null;

    const draft = await (dexfreight as any).Draft.edit(draftId, payload, token);
    await this.syncDraft(draftId, draft);
    return draft;
  }

  async publishDraftDrayage(draftId: string) {
    const token = await this.authService.token.get();
    if (!token) return null;

    const drayage = await (dexfreight as any).Draft.publish(draftId, token);
    await this.set(drayage);
    return drayage;
  }

  async setDraftBroker(draftId: string, brokerCompanyId: string | null) {
    const token = await this.authService.token.get();
    if (!token) return null;

    const draft = await (dexfreight as any).Draft.setBroker(draftId, brokerCompanyId, token);
    await this.syncDraft(draftId, draft);
    return draft;
  }

  async uploadDraftDocuments(draftId: string, documents: any[]) {
    const token = await this.authService.token.get();
    if (!token) return null;

    const result = await (dexfreight as any).Draft.uploadDocuments(draftId, documents, token);
    await this.syncDraft(draftId, result?.draft);
    return result;
  }

  async removeDraftDocument(draftId: string, documentId: string) {
    const token = await this.authService.token.get();
    if (!token) return null;

    const draft = await (dexfreight as any).Draft.removeDocument(draftId, documentId, token);
    await this.syncDraft(draftId, draft);
    return draft;
  }

  async getDraftEmailSource(draftId: string) {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await (dexfreight as any).EmailToLoad.getDrayageEmailSource(draftId, token);
  }

  /** La conversación del borrador con su asistente, y de dónde salió el borrador. */
  async getDraftFeedback(draftId: string): Promise<DraftFeedbackConversation | null> {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await (dexfreight as any).EmailToLoad.getDraftFeedback(draftId, token);
  }

  /** Deja un mensaje al asistente. Contesta en segundo plano: se lee con `getDraftFeedback`. */
  async sendDraftFeedback(draftId: string, message: string): Promise<DraftFeedbackTurn | null> {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await (dexfreight as any).EmailToLoad.sendDraftFeedback(draftId, message, token);
  }

  async syncDraft(draftId: string, draft?: DrayageFrontend) {
    if (!this.draftListeners[draftId]) this.draftListeners[draftId] = this.createDraftListener(draftId);
    return await this.draftListeners[draftId]?.sync(draft);
  }

  private createDraftListener(draftId: string) {
    return new CacheListener<DrayageFrontend | null>(
      null,
      async (draft?: DrayageFrontend | null) => draft ?? await this.getDraft(draftId)
    );
  }

  async sync(drayageId: string) {
    const drayage = await this.drayagesRepo.forceSync(drayageId);

    this.updates.next(drayage);

    log(`Drayage ${drayageId} synced from repository`);

    return drayage;
  }

  async set(drayage: DrayageFrontend) {
    await this.drayagesRepo.syncValue(drayage._id, drayage);

    this.updates.next(drayage);
    log(`Drayage ${drayage._id} synced from local object`);

    return drayage;
  }

  async get(drayageId: string) {
    return await this.drayagesRepo.value(drayageId);
  }

  clearAllDrayages() {
    for (const drayageId in this.drayages) {
      this.drayages[drayageId].unsubscribe();
      this.drayages[drayageId].clear();
    }
  }

  async getAllServices(token: string) {
    return await this.enumsService.drayageServices.get();
  }

  async getPermissionType() {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await dexfreight.Vehicles.getPermissionType(token);
  }

  async updateBoardingDetails(drayageId: string, payload: any) {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await dexfreight.Drayage.boardingDetails(drayageId, payload, token);
  }

  async assignVehicle(drayageId: string, vehicleId: string) {
    const token = await this.authService.token.get();
    if (!token) return;

    return await dexfreight.Drayage.updateDrayageVehicule(drayageId, vehicleId, token);
  }

  /**
   * Los remolques de la carga. Va aparte de `assignVehicle` porque son dos
   * decisiones independientes: se cambia de remolque sin tocar la unidad
   * motriz, y se asigna la unidad sin haber decidido todavía el remolque.
   *
   * Recibe la lista **completa**, no un añadir/quitar: mandar `[]` es la forma
   * de dejar la carga sin remolques.
   */
  async assignTrailers(drayageId: string, trailerIds: string[]) {
    const token = await this.authService.token.get();
    if (!token) return;

    return await dexfreight.Drayage.updateDrayageRemolques(drayageId, trailerIds, token);
  }

  async acceptRate(loadId: string, bidId?: string | null) {
    const token = await this.authService.token.get();
    if (!token) return null;

    return await dexfreight.Drayage.acceptRate(loadId, bidId, token);
  }

  getPaymentByState(state: string, payment): number {
    return ['new', 'open'].includes(state) ? payment?.open : payment?.close;
  }

  /**
   * It's to return the payment status of a drayage shipment
   * @param shipmentState
   * @returns
   */
  getPaymentStatus(shipmentState: string): 'open' | 'close' {
    return ['new', 'open', 'cancelled'].includes(shipmentState) ? 'open' : 'close';
  }

  /**
   * It's to search a service and return your quantity
   * @param serviceName
   */
  qtyService(services: any[], serviceName: string): number {
    return services?.find(service => service.type === serviceName)?.qty;
  }

  /**
   * It's to calculate the fsc service price
   */
  getPriceFsc(services: any[]): number {
    if (services?.length > 0) {
      let dry = services.find(service => service?.type.toLowerCase() === 'drayage');
      // let fsc = services.find(service => service?.type.toLowerCase() === 'fsc');

      !dry ? dry = { type: 'Drayage', rate: 0 } : null;
      // !fsc ? fsc = { type: 'FSC', rate: 0 } : null;

      // return Number(dry.rate * fsc.rate / 100);
      return Number(dry.rate)
    }
  }

  /**
   * It's to calculate the total price of a service
   */
  totalServicePrice(service, allServices, fixedServices?): number {
    const { type, qty, rate } = service;
    const serviceQuantity = qty || service.quantity || 1;

    let total = 0;

    if (type && type.toLowerCase() === 'fsc') {
      total += this.getPriceFsc(fixedServices) * serviceQuantity;
    } else {
      total += rate * serviceQuantity;
    }

    return total;
  }

  /**
   * It's to calculate the total price of services without allServices array
   */
  getTotalPriceServices(services: any[], otherServices?: any[]): number {
    let total = 0;

    if (services?.length > 0) {
      total += this.calculateServiceTotal(services);
    }

    if (otherServices?.length > 0) {
      total += this.calculateOtherServiceTotal(otherServices);
    }

    return Number(total);
  }

  /**
   * It's to calculate the total price of the drayage
   */
  totalDrayagePrice(allServices: any[], services: any[], otherServices?: any[]): number {
    let total = 0;

    if (services?.length > 0) {
      total += this.calculateServiceTotal(services);
    }

    if (otherServices?.length > 0) {
      total += this.calculateOtherServiceTotal(otherServices);
    }

    return Number(total);
  }

  calculateServiceTotal(services: any[]): number {
    let total = 0;

    services.forEach(service => {
      const { qty, rate } = service;
      const serviceQuantity = (qty || service.quantity) || 1;

      total += Number(rate) * Number(serviceQuantity);
    });

    return Number(total);
  }

  calculateOtherServiceTotal(otherServices: any[]): number {
    let total = 0;

    otherServices.forEach(otherService => {
      total += Number(otherService.rate) * Number(otherService.qty || otherService.quantity);
    });

    return Number(total);
  }


  /**
   * It's to search a bid in an all negotitation of a drayage
   */
  bidSearch(bidId: string, negotiations?: any[]): boolean {
    if (negotiations?.length > 0) {
      return negotiations.find(bid => bid?.id === bidId);
    }
  }

  acceptedNewCharges(load) {
    return dexfreight.Drayage.acceptedService(load._id, localStorage.token);
  }

  async getDrayageDetails(drayageId: string): Promise<any> {
    try {
      return await dexfreight.Drayage.get(drayageId, localStorage.token);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Function to calculate the Drayage rate of ship
   * @param load
   */
  getRateLoad(load: any) {
    return ['new', 'open'].includes(load?.state)
      ? this.getInitialRate(load)
      : Number(load?.payment?.rate || load?.payment?.close?.rate || this.totalDrayagePrice(this.allServices, load?.payment?.close?.services, load?.payment?.close?.otherServices))
  }

  /**
   * @description Get total rate of drayage.
   * @returns {String} If rate is 0 we return 'Negotiable'.
   */
  getInitialRate(load) {
    try {
      let rateDrayage = this.totalDrayagePrice(this.allServices, load?.payment?.open?.services, load?.payment?.open?.otherServices);
      return rateDrayage !== 0 ? rateDrayage : 0;
    } catch (e) {
      console.error(e)
    }
  }

  async preloadDrayages(page: number, myDrayages: boolean = false) {
    const result = await this.getDrayages({
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      filters: {},
      myDrayages
    });
    return result;
  }

  async getDrayages({ skip, limit, filters, myDrayages }) {
    const drayages = await this.loadDrayages(skip, myDrayages, filters, limit);

    for (const drayage of drayages) {
      const containers = drayage.containers || [];

      drayage.integration = containers.some(container => container?.liberationTransaction);
      drayage.rate = this.getRateLoad(drayage);

      if (drayage.integration) {
        drayage.containersNumber = containers.map(container => container?.containerNumber).filter(id => id);
        drayage.liberationsIds = containers.map(container => container?.liberationTransaction?.id).filter(id => id);
      }
    }

    if (skip === 0) {
      DataCache.set(`drayageList/initialDrayages/${myDrayages}`, drayages, DataCache.Long)
    }


    return drayages;
  }

  async getDraftDrayages({ skip, limit }) {
    const token = await this.authService.token.get();
    if (!token) return [];

    const drafts = await (dexfreight as any).Draft.list({ skip, limit }, token);

    for (const draft of drafts) {
      draft.rate = this.getRateLoad(draft);
    }

    return drafts;
  }

  async loadDrayages(skip: number = 0, myDrayages: boolean = false, filters: any = {}, limit: number = PAGE_SIZE) {
    const filtersFormatted = this.transformFilters(filters)
    const cartaPorteFilter = filters?.cartaPorte?.value;

    delete filters.cartaPorte
    // @ts-ignore
    const obs = await this.micro.Obs.Drayage.Rx.availableDrayages(filtersFormatted, { available: myDrayages, skip, limit, cartaPorte: cartaPorteFilter, includeUnpublished: myDrayages }, localStorage.getItem('token'));
    const shipments = await firstValueFrom(obs.pipe(
      distinct((shipment) => shipment._id),
      toArray()
    ));

    for (const drayage of shipments) {
      this.set(drayage);
    }
    return shipments;
  }

  transformFilters(filters: any) {
    const transformed = {};
    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (typeof filterValue === 'object' && filterValue !== null && filterValue.value !== undefined) {
        transformed[key] = filterValue.value;
      } else {
        transformed[key] = filterValue;
      }
    });
    return transformed;
  };

  get events() {
    return this.eventsList;
  }

  async assignDriver(drayageId: string, driverId: string) {
    const token = await this.authService.token.get();
    const modified = await dexfreight.Drayage.booking(drayageId, driverId, token) as any;
    if (!modified) (await this.observer(drayageId)).sync();
  }

}
